import './Takes.css';
import React, { useRef, useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Take from './Take.jsx';
import BigQuestions from './BigQuestions.jsx';
import { useTranslation } from 'react-i18next';

import takesData from './takesData.json';
import sessionData from './sessionData.json';
import vorData from './vorData.json';

function getAgreePct(votes) {
  const total = votes.number_agree + votes.number_disagree;
  if (total === 0) return 0;
  return Math.round((votes.number_agree / total) * 100);
}

const VELOCITY_THRESHOLD = 0.15;
const DISTANCE_THRESHOLD = 20;


export default function Takes() {
  const { t, i18n } = useTranslation();
  const history  = useHistory();
  const location = useLocation();
  const lang = i18n.language?.slice(0, 2) || 'en';

  const loc = (obj, key) => {
    if (lang === 'de' && obj[`${key}_de`]) return obj[`${key}_de`];
    if (lang === 'fr' && obj[`${key}_fr`]) return obj[`${key}_fr`];
    return obj[key] ?? obj[`${key}_en`] ?? '';
  };

  const session = sessionData.sessions[0];

  const [currentTakeIndex, setCurrentTakeIndex] = useState(0);
  const [userVotes, setUserVotes]               = useState({});
  const [highlightTopicId]   = useState((location.state || {}).highlightTopicId   || null);
  const [highlightThesisId]  = useState((location.state || {}).highlightThesisId  || null);

  const mountPathRef = useRef(location.pathname);
  const view = mountPathRef.current === '/theses' ? 'questions' : 'live';

  const filteredTakes = takesData.data;

  const trackRef = useRef(null);
  const idxRef   = useRef(0);

  useEffect(() => { idxRef.current = currentTakeIndex; }, [currentTakeIndex]);

  // ── Measure fixed-element heights → CSS variables ──
  // Only run in live view: the questions view mounts simultaneously during the
  // route transition (TransitionGroup keeps both), and its dock is inside
  // display:none which would zero out --takes-dock-h, shifting the old slide.
  useEffect(() => {
    if (view !== 'live') return;
    const measure = () => {
      const navbar     = document.querySelector('.navbar');
      const toggleBar  = document.querySelector('.takes-toggle-bar');
      const hnBanner   = document.querySelector('.takes-hn-banner');
      const voteDock   = document.querySelector('.takes-vote-dock');
      if (navbar && voteDock) {
        const root    = document.documentElement;
        const navH    = navbar.getBoundingClientRect().height;
        const togH    = toggleBar ? toggleBar.getBoundingClientRect().height : 0;
        const bannerH = hnBanner  ? hnBanner.getBoundingClientRect().height  : 0;
        root.style.setProperty('--takes-nav-h',  (navH + togH + bannerH) + 'px');
        root.style.setProperty('--takes-dock-h', voteDock.getBoundingClientRect().height + 'px');
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // ── Low-level slide move ──
  // snapBack=true uses a shorter, lighter ease for rubber-banding back to current slide;
  // false (default) uses the full navigation easing.
  const moveTo = (idx, animated = true, snapBack = false) => {
    const track = trackRef.current;
    if (!track) return;
    if (animated) {
      // Flush layout so the browser commits the current dragged transform
      // before we set the new transition - otherwise both style changes land
      // in the same frame and the animation has no "from" state to start from.
      track.getBoundingClientRect();
      track.style.transition = snapBack
        ? 'transform 0.7s cubic-bezier(0.16, 1, 0.3, 1)'   // expo-out: long tail for snap-back
        : 'transform 0.9s cubic-bezier(0.16, 1, 0.3, 1)'; // expo-out: long tail for navigation
    } else {
      track.style.transition = 'none';
    }
    track.style.transform = `translateY(calc(-${idx} * 100dvh))`;
  };

  // ── Navigate ──
  const navigateTo = (idx) => {
    const clamped = Math.max(0, Math.min(filteredTakes.length - 1, idx));
    // Commit the current mid-animation position before starting a new animation
    // so rapid swipes pick up from wherever the slide actually is, not the target.
    const track = trackRef.current;
    if (track) {
      const live = getComputedStyle(track).transform;
      track.style.transition = 'none';
      track.style.transform = live;
    }
    setCurrentTakeIndex(clamped);
    idxRef.current = clamped;
    moveTo(clamped, true);
  };

  const goToSlide = (idx) => navigateTo(idx);

  // ── Touch & wheel gesture handler ── (live mode only)
  useEffect(() => {
    if (view !== 'live') return;
    const touch = { startY: 0, lastY: 0, lastT: 0, velocity: 0 };

    // ── TOUCH ──
    const onTouchStart = (e) => {
      touch.startY   = e.touches[0].clientY;
      touch.lastY    = touch.startY;
      touch.lastT    = Date.now();
      touch.velocity = 0;
    };

    const onTouchMove = (e) => {
      e.preventDefault();
      const now = Date.now();
      const y   = e.touches[0].clientY;
      const dt  = now - touch.lastT;
      if (dt > 0) touch.velocity = (touch.lastY - y) / dt;
      touch.lastY = y;
      touch.lastT = now;
      // Live drag: move the track with the finger so the user sees "play"
      const dragOffset = touch.startY - y;
      const track = trackRef.current;
      if (track) {
        track.style.transition = 'none';
        track.style.transform = `translateY(calc(-${idxRef.current} * 100dvh - ${dragOffset}px))`;
      }
    };

    const onTouchEnd = () => {
      const distance = touch.startY - touch.lastY;
      const triggered =
        Math.abs(touch.velocity) > VELOCITY_THRESHOLD ||
        Math.abs(distance)       > DISTANCE_THRESHOLD;
      if (triggered) {
        navigateTo(idxRef.current + (distance > 0 ? 1 : -1));
      } else {
        moveTo(idxRef.current, true, true); // snapBack
      }
    };

    // ── WHEEL (desktop) ──
    let wheelTimer = null;
    let wheelDelta = 0;
    const onWheel = (e) => {
      e.preventDefault();
      wheelDelta += e.deltaY;
      // Live visual feedback
      const track = trackRef.current;
      if (track) {
        track.style.transition = 'none';
        track.style.transform = `translateY(calc(-${idxRef.current} * 100dvh - ${wheelDelta}px))`;
      }
      clearTimeout(wheelTimer);
      wheelTimer = setTimeout(() => {
        const triggered = Math.abs(wheelDelta) > DISTANCE_THRESHOLD;
        if (triggered) {
          navigateTo(idxRef.current + (wheelDelta > 0 ? 1 : -1));
        } else {
          moveTo(idxRef.current, true, true); // snapBack
        }
        wheelDelta = 0;
      }, 80);
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true  });
    document.addEventListener('touchmove',  onTouchMove,  { passive: false });
    document.addEventListener('touchend',   onTouchEnd,   { passive: true  });
    document.addEventListener('wheel',      onWheel,      { passive: false });

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove',  onTouchMove);
      document.removeEventListener('touchend',   onTouchEnd);
      document.removeEventListener('wheel',      onWheel);
      clearTimeout(wheelTimer);
    };
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVote = (vote) => {
    setUserVotes((prev) => ({ ...prev, [safeIndex]: vote }));
  };

  const safeIndex       = Math.min(currentTakeIndex, Math.max(0, filteredTakes.length - 1));
  const currentTake     = filteredTakes[safeIndex];
  const currentUserVote = userVotes[safeIndex];
  const dockType        = currentTake.type;
  const hasNoDock       = dockType === 'partner' || dockType === 'report' || dockType === 'battle' || dockType === 'news';

  const agendaLeadingLabel = (() => {
    const l = currentTake.leading;
    if (!l) return '';
    return lang === 'de' ? l.label_de : lang === 'fr' ? l.label_fr : l.label_en;
  })();

  return (
    <div className={`takes-root${view === 'questions' ? ' questions-active' : ''}`}>

      {/* ── Toggle bar ── */}
      <div className={`takes-toggle-bar${hasNoDock ? ' hidden' : ''}`}>
        <div className="takes-seg-control">
          <button
            className={`takes-seg-btn${view === 'live' ? ' active' : ''}`}
            onClick={() => history.push('/feed')}
          >
            {t('nav.live')}
          </button>
          <button
            className={`takes-seg-btn${view === 'questions' ? ' active' : ''}`}
            onClick={() => history.push('/theses')}
          >
            {t('nav.bigQuestions')}
          </button>
        </div>
      </div>

      {/* ── Happening Now banner - always visible on both Live and Big Questions ── */}
      <div className="takes-hn-banner" onClick={() => history.push('/session/townhall-oxford')}>
        <div className="takes-hn-inner">
          <div className="takes-hn-badge">
            <span className="takes-hn-dot" />
            <span className="takes-hn-label">{t('nav.now')}</span>
          </div>
          <div className="takes-hn-divider" />
          <div className="takes-hn-text">
            <span className="takes-hn-type">{loc(session, 'type')}</span>
            <span className="takes-hn-title">{loc(session, 'title')}</span>
          </div>
          <svg className="takes-hn-chevron" width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 3l5 5-5 5"/>
          </svg>
        </div>
      </div>

      <div className={view !== 'live' ? 'takes-view-hidden' : ''}>
        {/* ── Sliding track ── */}
        <div className="takes-slides-track" ref={trackRef}>
          {filteredTakes.map((take, index) => (
            <Take
              key={`${take.id}-${index}`}
              take={take}
              onViewerTakesClick={() => history.push('/session/townhall-oxford')}
              onRelatedTopicClick={(topicId) => {
                history.push('/theses', { highlightTopicId: topicId });
              }}
              onThesisTagClick={(thesisId) => {
                const thesis = vorData.theses.find(th => th.id === thesisId);
                history.push('/theses', {
                  highlightTopicId: thesis?.topicId || null,
                  highlightThesisId: thesisId,
                });
              }}
              vote={userVotes[index]}
              onVote={(v) => setUserVotes(prev => ({ ...prev, [index]: v }))}
              onRecapClick={take.sessionId ? () => history.push(`/session/${take.sessionId}`) : undefined}
            />
          ))}
        </div>

        {/* ── Progress dots ── */}
        <div className="takes-dots">
          {filteredTakes.map((_, i) => (
            <div
              key={i}
              className={`takes-dot${i === safeIndex ? ' active' : ''}`}
              onClick={() => goToSlide(i)}
            />
          ))}
        </div>

        {/* ── Bottom dock - hidden for partner and report slides ── */}
        <div className={`takes-vote-dock${hasNoDock ? ' hidden' : ''}`}>
          <div className="takes-vote-dock-inner">

            {/* ── TAKE / NEWS: agree-disagree + Munich Voices reveal ── */}
            {(dockType === 'take' || dockType === 'news') && (<>
              {currentUserVote ? (
                <div className="dock-mv-reveal">
                  <div className="dock-mv-stat">
                    <span className="dock-mv-pct">{currentTake.agree ?? 64}%</span>
                    <span className="dock-mv-label">Agree</span>
                  </div>
                  <div className="dock-mv-footer">
                    <span className="dock-mv-name">
                      Munich Voices &middot; {(currentTake.total ?? 0).toLocaleString()} reactions
                    </span>
                    {currentTake.groupVotes && (
                      <button
                        className="dock-mv-detail"
                        onClick={() => history.push(`/takes/${currentTake.id}/votes`)}
                      >
                        Detailed results &rarr;
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="dock-prevote">
                  <span className="dock-prevote-text">{t('takes.voteToSee')}</span>
                </div>
              )}
              <div className="dock-vote">
                <button
                  className={`vote-btn v-agree${currentUserVote === 'agree' ? ' on' : ''}${currentUserVote === 'disagree' ? ' dim' : ''}`}
                  onClick={() => handleVote('agree')}
                >
                  <svg viewBox="0 0 16 16"><path d="M8 3l5.5 9H2.5z" /></svg>
                  Agree
                </button>
                <button
                  className={`vote-btn v-disagree${currentUserVote === 'disagree' ? ' on' : ''}${currentUserVote === 'agree' ? ' dim' : ''}`}
                  onClick={() => handleVote('disagree')}
                >
                  <svg viewBox="0 0 16 16"><path d="M8 13L2.5 4h11z" /></svg>
                  Disagree
                </button>
              </div>
            </>)}

            {/* ── QUESTION: agenda option pills + Munich Voices leading-option reveal ── */}
            {dockType === 'question' && (
              currentUserVote ? (
                <div className="dock-mv-reveal">
                  <div className="dock-mv-stat">
                    <span className="dock-mv-pct">{currentTake.leading?.pct ?? 41}%</span>
                    <span className="dock-mv-label">Most-picked topic so far</span>
                  </div>
                  <div className="dock-mv-footer">
                    <span className="dock-mv-name">{agendaLeadingLabel} &middot; Munich Voices</span>
                  </div>
                </div>
              ) : (<>
                <div className="dock-prevote">
                  <span className="dock-prevote-text">Vote to see the most-requested topic so far.</span>
                </div>
                <div key={safeIndex} className="dock-agenda">
                  {(currentTake.agendaOptions ?? []).map(opt => {
                    const label = lang === 'de' ? opt.label_de : lang === 'fr' ? opt.label_fr : opt.label_en;
                    return (
                      <button
                        key={opt.id}
                        className="dock-agenda-pill"
                        onClick={() => handleVote(opt.id)}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </>)
            )}

            {/* ── PREVIEW: remind-me button ── */}
            {dockType === 'preview' && (
              currentUserVote === 'reminded' ? (
                <div className="dock-preview-confirm">
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                    <path d="M3 8l4 4 6-6" />
                  </svg>
                  You'll be reminded · {loc(currentTake, 'when')}
                </div>
              ) : (
                <button className="dock-preview-btn" onClick={() => handleVote('reminded')}>
                  Remind me · {loc(currentTake, 'when')}
                </button>
              )
            )}

            {/* ── RECAP: link to full session results ── */}
            {dockType === 'recap' && (
              <button
                className="dock-recap-btn"
                onClick={() => history.push(`/session/${currentTake.sessionId}`)}
              >
                Full results from {currentTake.eventName_en?.split(' · ')[1] ?? 'the event'}
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                  <path d="M6 3l5 5-5 5" />
                </svg>
              </button>
            )}

          </div>
          <div className="takes-proto">Prototype by Ferdinand Riedl · riedlsoftware.com</div>
        </div>

        {/* Partner / report slides: proto label without dock */}
        <div className={`takes-proto takes-proto-partner${hasNoDock ? '' : ' hidden'}`}>Prototype by Ferdinand Riedl · riedlsoftware.com</div>
      </div>

      <div className={view !== 'questions' ? 'takes-view-hidden' : ''}>
        <BigQuestions highlightTopicId={highlightTopicId} highlightThesisId={highlightThesisId} />
      </div>

    </div>
  );
}
